import React, { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "antd";
import "../EditableCellGrid.css";

function normalizeEditorValue(column, value, row) {
    if (column.normalize) return column.normalize(value, row);
    return value;
}

function getEditorPlaceholder(column) {
    return column.editorOptions?.placeholder ?? column.placeholder;
}

function getTextareaAutoSize(column) {
    const minRows = column.editorOptions?.minRows ?? column.minRows ?? 3;
    const maxRows = column.editorOptions?.maxRows ?? column.maxRows;
    return maxRows === undefined ? { minRows } : { minRows, maxRows };
}

function shouldSelectOnFocus(column) {
    return column.editorOptions?.selectOnFocus !== false;
}

function hasInitialValue(column) {
    return column.editorOptions?.hasInitialValue === true;
}

function shouldCommitOnEnter(column) {
    return column.editorOptions?.commitOnEnter === true || column.commitOnEnter === true;
}

function getTextAreaElement(inputRef) {
    return inputRef.current?.resizableTextArea?.textArea ?? inputRef.current;
}

function focusInitialValue(inputRef, selectOnFocus) {
    const textAreaElement = getTextAreaElement(inputRef);
    textAreaElement?.focus?.();
    if (selectOnFocus) {
        textAreaElement?.select?.();
        return;
    }

    const valueLength = String(textAreaElement?.value ?? "").length;
    textAreaElement?.setSelectionRange?.(valueLength, valueLength);
}

const TextareaCellEditor = ({ row, column, onRowChange, onClose }) => {
    const initialValue = row[column.key] ?? "";
    const [value, setValue] = useState(String(initialValue));
    const closedRef = useRef(false);
    const isComposingRef = useRef(false);
    const initialDraftSyncedRef = useRef(false);
    const inputRef = useRef(null);
    const selectOnFocus = shouldSelectOnFocus(column);

    useEffect(() => {
        focusInitialValue(inputRef, selectOnFocus);
    }, [selectOnFocus]);

    const syncRowValue = useCallback(
        (nextValue) => {
            const nextRowValue = normalizeEditorValue(column, nextValue, row);
            onRowChange({ ...row, [column.key]: nextRowValue });
        },
        [column, onRowChange, row],
    );

    useEffect(() => {
        if (!hasInitialValue(column) || initialDraftSyncedRef.current) return;
        initialDraftSyncedRef.current = true;
        const nextRowValue = normalizeEditorValue(column, row[column.key] ?? "", row);
        onRowChange({ ...row, [column.key]: nextRowValue }, false);
    }, [column, onRowChange, row]);

    const commit = useCallback((shouldFocusCell = false) => {
        if (closedRef.current) return;
        closedRef.current = true;
        onClose(true, shouldFocusCell);
    }, [onClose]);

    const handleChange = useCallback(
        (event) => {
            const nextValue = event.target.value;
            setValue(nextValue);
            if (!isComposingRef.current && !event.nativeEvent?.isComposing) {
                syncRowValue(nextValue);
            }
        },
        [syncRowValue],
    );

    const handleCompositionStart = useCallback(() => {
        isComposingRef.current = true;
    }, []);

    const handleCompositionEnd = useCallback(
        (event) => {
            isComposingRef.current = false;
            const nextValue = event.currentTarget.value;
            setValue(nextValue);
            syncRowValue(nextValue);
        },
        [syncRowValue],
    );

    return (
        <Input.TextArea
            ref={inputRef}
            autoFocus
            autoSize={getTextareaAutoSize(column)}
            className="editable-cell-editor editable-cell-editor-textarea ant-input"
            value={value}
            placeholder={getEditorPlaceholder(column)}
            onChange={handleChange}
            onCompositionStart={handleCompositionStart}
            onCompositionEnd={handleCompositionEnd}
            onBlur={() => commit(false)}
            onKeyDown={(event) => {
                if (isComposingRef.current || event.nativeEvent?.isComposing) {
                    if (event.key === "Enter" || event.key === "Tab") event.stopPropagation();
                    return;
                }
                if (event.key === "Enter" && (event.shiftKey || event.altKey)) {
                    event.stopPropagation();
                } else if (event.key === "Enter" && (shouldCommitOnEnter(column) || event.ctrlKey || event.metaKey)) {
                    event.preventDefault();
                    event.stopPropagation();
                    commit(true);
                } else if (event.key === "Enter") {
                    event.stopPropagation();
                }
            }}
        />
    );
};

export default TextareaCellEditor;
